import dayjs from 'dayjs';
import React, {
  useCallback, useContext, useEffect, useMemo,
  useState,
} from 'react';
import {
  AddressContext, AuthContext, CartContext, CateringContext,
} from 'constants/contexts';
import useEventEffect from 'hooks/useEvent';
import { EVENTS } from 'constants/events';
import { useHistory } from 'react-router-dom';
import usePersistedState from 'hooks/usePersistedState';
import { SessionJsonStorage } from 'utils/storage';
import { env } from 'constants';
import ScheduleOrderOverlay from './ScheduleOrderOverlay';
import HeadcountOverlay from './HeadcountOverlay';
import DeliveryInstructionOverlay from './DeliveryInstructionOverlay';
import DeliveryForm from './DeliverForm';
import { PartnerLogos } from './constants';
import CartAction from './CartAction';

const INITIAL_STATE = {
  headcount: 0,
  recipient: null,
  checkoutStarted: false,
  addressConfirmed: false,
  deliveryInstructionsConfirmed: false,
};
function CateringProvider({ children }) {
  const [state, setState] = usePersistedState(INITIAL_STATE, {
    key: 'catering-provider',
    storage: SessionJsonStorage,
  });
  const cart = useContext(CartContext);
  const { isLoggedIn, profileIsComplete } = useContext(AuthContext);
  const address = useContext(AddressContext);
  const [headcountOpen, setHeadcountOpen] = useState(false);
  const [schedulingOpen, setSchedulingOpen] = useState(false);
  const [deliveryInstructionsOpen, setDeliveryInstructionsOpen] = useState(false);
  const updateState = useCallback((payload) => { setState((c) => ({ ...c, ...payload })); }, []);
  const history = useHistory();
  const continueToCheckout = useCallback(() => {
    if (!state.checkoutStarted) {
      updateState({ checkoutStarted: true });
    }
    if (!isLoggedIn || !profileIsComplete) {
      return;
    }
    if (!state.addressConfirmed && cart.hasDelivery) {
      setTimeout(() => {
        address.openModal();
      }, 50);
      return;
    }
    if (state.deliveryInstructionsConfirmed) {
      setTimeout(() => {
        history.push('/order/checkout');
        updateState({
          checkoutStarted: false,
          addressConfirmed: false,
          deliveryInstructionsConfirmed: false,
        });
      }, 100);
    } else {
      setTimeout(() => {
        setDeliveryInstructionsOpen(true);
      }, 200);
    }
  }, [
    state.deliveryInstructionsConfirmed,
    state.addressConfirmed,
    state.checkoutStarted,
    isLoggedIn,
    cart.hasDelivery,
    profileIsComplete,
  ]);

  const contextValue = useMemo(() => ({
    ...state,
    set(payload) { setState((c) => ({ ...c, ...payload })); },
    openHeadcount() { setHeadcountOpen(true); },
    openScheduling() { setSchedulingOpen(true); },
    reset() { setState(INITIAL_STATE); },
    continueToCheckout,
    PartnerLogos,
  }), [state, continueToCheckout]);

  useEventEffect(() => {
    if (state.checkoutStarted) {
      updateState({ addressConfirmed: true });
    }
  }, EVENTS.SELECT_ADDRESS, [state.checkoutStarted]);

  useEventEffect(() => {
    setState(INITIAL_STATE);
  }, EVENTS.SIGN_OUT);

  useEffect(() => {
    if (!env.IS_CATERING || !cart.merchant) {
      return;
    }
    if (!cart.scheduledTime || (cart.firstAvailableTime && dayjs(cart.firstAvailableTime) > dayjs(cart.scheduledTime))) {
      cart.setScheduledTime(cart.firstAvailableTime);
      cart.ensureMenuIsAvailable(cart.firstAvailableTime);
    }
  }, [cart.scheduledTime, cart.firstAvailableTime, cart.merchant]);

  useEffect(() => {
    if (!isLoggedIn || !env.IS_CATERING || !state.checkoutStarted) {
      return;
    }
    continueToCheckout();
  }, [
    continueToCheckout,
    isLoggedIn,
    state.checkoutStarted,
  ]);

  return (
    <CateringContext.Provider value={contextValue}>
      {children}
      {
        env.IS_CATERING && (
          <>
            <ScheduleOrderOverlay
              open={schedulingOpen}
              onClose={() => setSchedulingOpen(false)}
            />
            <HeadcountOverlay
              open={headcountOpen}
              onClose={() => setHeadcountOpen(false)}
            />
            <DeliveryInstructionOverlay
              open={deliveryInstructionsOpen}
              onClose={() => setDeliveryInstructionsOpen(false)}
            />
          </>
        )
      }
    </CateringContext.Provider>
  );
}

CateringProvider.useCatering = () => useContext(CateringContext);
CateringProvider.DeliveryForm = DeliveryForm;
CateringProvider.CartAction = CartAction;

export default CateringProvider;
